import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BROWNIE_TYPES, BOX_OPTIONS, WHATSAPP_LINK } from "../../lib/constants";
import { Package, Plus, Minus, Trash2, ShoppingBag, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const BoxBuilderSection = ({ onAddToCart }) => {
  const [selectedBox, setSelectedBox] = useState(BOX_OPTIONS[2]); // Default to box of 5
  const [boxContents, setBoxContents] = useState([]);
  const [isDecorated, setIsDecorated] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const addBrownie = (brownie) => {
    if (boxContents.length >= selectedBox.quantity) {
      toast.error(`Tu caja de ${selectedBox.quantity} ya está llena`);
      return;
    }
    setBoxContents((prev) => [...prev, brownie]);
    
    // Small celebration
    confetti({
      particleCount: 15,
      spread: 40,
      origin: { y: 0.7 },
      colors: ["#C88037", "#2D1810"],
    });
  };

  const removeBrownie = (index) => {
    setBoxContents((prev) => prev.filter((_, i) => i !== index));
  };

  const clearBox = () => {
    setBoxContents([]);
  };

  const getBoxPrice = () => {
    return isDecorated ? selectedBox.decoratedPrice : selectedBox.normalPrice;
  };

  const handleAddBoxToCart = () => {
    if (boxContents.length < selectedBox.quantity) {
      toast.error(`Completa tu caja con ${selectedBox.quantity} brownies`);
      return;
    }

    const boxItem = {
      id: `custom-box-${Date.now()}`,
      name: `${selectedBox.name} Personalizada${isDecorated ? " (Decorados)" : ""}`,
      image: boxContents[0].image,
      price: getBoxPrice(),
      contents: boxContents.map((b) => b.name).join(", "),
    };

    onAddToCart(boxItem);
    
    // Big celebration!
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#C88037", "#2D1810", "#E5984A"],
    });

    toast.success("¡Caja agregada a tu pedido!");
    clearBox();
  };

  const generateWhatsAppMessage = () => {
    const contents = boxContents.map((b) => b.name).join(", ");
    const message = `¡Hola Urban Brownies! 🍫\n\nQuiero armar una ${selectedBox.name}${isDecorated ? " con brownies decorados" : ""}:\n\n📦 Contenido: ${contents}\n💰 Total: ${formatPrice(getBoxPrice())}\n\n¿Está disponible?`;
    return encodeURIComponent(message);
  };

  return (
    <section
      id="arma-tu-caja"
      className="py-20 md:py-32 bg-[#FDFBF7]"
      data-testid="box-builder-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#C88037] font-medium text-sm uppercase tracking-wider">
            Crea tu Mix Perfecto
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D1810] mt-2 mb-4">
            Arma tu <span className="italic text-[#C88037]">caja soñada</span>
          </h2>
          <p className="text-[#5E4B3C] max-w-2xl mx-auto">
            Elige el tamaño de tu caja y llénala con tus brownies favoritos. 
            ¡Tú decides la combinación perfecta!
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left: Brownie Selection */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Box Size Selection */}
            <div className="bg-[#F4EDE4] rounded-2xl p-6">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                1. Elige el tamaño de tu caja
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {BOX_OPTIONS.map((box) => (
                  <button
                    key={box.id}
                    onClick={() => {
                      setSelectedBox(box);
                      if (boxContents.length > box.quantity) {
                        setBoxContents(boxContents.slice(0, box.quantity));
                      }
                    }}
                    className={`p-3 rounded-xl text-center transition-all ${
                      selectedBox.id === box.id
                        ? "bg-[#C88037] text-white shadow-lg"
                        : "bg-white text-[#2D1810] hover:bg-[#E5DCD0]"
                    }`}
                    data-testid={`select-${box.id}`}
                  >
                    <div className="font-bold text-lg">{box.quantity}</div>
                    <div className="text-xs opacity-80">uds</div>
                  </button>
                ))}
              </div>
              
              {/* Decorated Toggle */}
              <div className="flex items-center justify-between mt-4 p-3 bg-white rounded-xl">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                  <span className="text-[#2D1810] font-medium">Brownies decorados</span>
                </div>
                <button
                  onClick={() => setIsDecorated(!isDecorated)}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    isDecorated ? "bg-[#C88037]" : "bg-[#E5DCD0]"
                  }`}
                  data-testid="toggle-decorated"
                >
                  <motion.div
                    animate={{ x: isDecorated ? 28 : 4 }}
                    className="absolute top-1 w-5 h-5 bg-white rounded-full shadow"
                  />
                </button>
              </div>

              <div className="text-center mt-4 text-[#C88037] font-bold text-xl">
                {formatPrice(getBoxPrice())}
              </div>
            </div>

            {/* Brownie Types */}
            <div className="bg-[#F4EDE4] rounded-2xl p-6">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4">
                2. Agrega brownies a tu caja
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {BROWNIE_TYPES.map((brownie) => (
                  <motion.button
                    key={brownie.id}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => addBrownie(brownie)}
                    className="relative bg-white rounded-xl overflow-hidden group"
                    data-testid={`add-brownie-${brownie.id}`}
                  >
                    <div className="aspect-square">
                      <img
                        src={brownie.image}
                        alt={brownie.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-[#2D1810]/0 group-hover:bg-[#2D1810]/40 transition-colors flex items-center justify-center">
                        <Plus className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="p-2 text-center">
                      <span className="text-sm font-medium text-[#2D1810]">{brownie.name}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: Box Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:sticky lg:top-24"
          >
            <div className="bg-[#F4EDE4] rounded-2xl p-6 h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl text-[#2D1810]">Tu Caja</h3>
                <span className="text-[#5E4B3C] text-sm">
                  {boxContents.length}/{selectedBox.quantity}
                </span>
              </div>

              {/* Box Grid */}
              <div 
                className={`grid gap-2 mb-6 ${
                  selectedBox.quantity <= 4 ? "grid-cols-2" : 
                  selectedBox.quantity <= 6 ? "grid-cols-3" : "grid-cols-4"
                }`}
              >
                {Array.from({ length: selectedBox.quantity }).map((_, index) => (
                  <motion.div
                    key={index}
                    initial={false}
                    animate={{
                      scale: boxContents[index] ? 1 : 0.95,
                      opacity: boxContents[index] ? 1 : 0.5,
                    }}
                    className={`aspect-square rounded-xl border-2 border-dashed overflow-hidden ${
                      boxContents[index]
                        ? "border-[#C88037] bg-white"
                        : "border-[#E5DCD0] bg-white/50"
                    }`}
                    data-testid={`box-slot-${index}`}
                  >
                    {boxContents[index] ? (
                      <div className="relative w-full h-full group">
                        <img
                          src={boxContents[index].image}
                          alt={boxContents[index].name}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => removeBrownie(index)}
                          className="absolute inset-0 bg-[#D9381E]/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          data-testid={`remove-slot-${index}`}
                        >
                          <Trash2 className="w-6 h-6 text-white" strokeWidth={1.5} />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-[#2D1810]/80 text-white text-xs p-1 text-center truncate">
                          {boxContents[index].name}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#E5DCD0]">
                        <Plus className="w-6 h-6" strokeWidth={1.5} />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-[#5E4B3C] mb-2">
                  <span>Progreso</span>
                  <span>{Math.round((boxContents.length / selectedBox.quantity) * 100)}%</span>
                </div>
                <div className="h-2 bg-[#E5DCD0] rounded-full overflow-hidden">
                  <motion.div
                    initial={false}
                    animate={{ width: `${(boxContents.length / selectedBox.quantity) * 100}%` }}
                    className="h-full bg-gradient-to-r from-[#C88037] to-[#E5984A] rounded-full"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddBoxToCart}
                  disabled={boxContents.length < selectedBox.quantity}
                  className={`w-full py-4 rounded-full font-semibold flex items-center justify-center gap-2 transition-colors ${
                    boxContents.length === selectedBox.quantity
                      ? "bg-[#C88037] text-white hover:bg-[#E5984A]"
                      : "bg-[#E5DCD0] text-[#5E4B3C] cursor-not-allowed"
                  }`}
                  data-testid="add-box-to-cart"
                >
                  <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                  {boxContents.length === selectedBox.quantity
                    ? "Agregar al Pedido"
                    : `Faltan ${selectedBox.quantity - boxContents.length} brownies`}
                </motion.button>

                <motion.a
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  href={`${WHATSAPP_LINK}?text=${generateWhatsAppMessage()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full py-4 rounded-full font-semibold flex items-center justify-center gap-2 transition-colors ${
                    boxContents.length > 0
                      ? "bg-[#25D366] text-white hover:bg-[#20BD5A]"
                      : "bg-[#E5DCD0] text-[#5E4B3C] pointer-events-none"
                  }`}
                  data-testid="order-box-whatsapp"
                >
                  Pedir por WhatsApp
                </motion.a>

                {boxContents.length > 0 && (
                  <button
                    onClick={clearBox}
                    className="w-full text-[#5E4B3C] text-sm hover:text-[#D9381E] transition-colors"
                    data-testid="clear-box"
                  >
                    Vaciar caja
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BoxBuilderSection;
